import * as React from "react";
import { Slot } from "@radix-ui/react-slot"; // Import Slot for asChild prop
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils"; // Corrected import path

// Update CVA definition slightly for better structure
const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 hover:font-medium",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:font-medium",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:font-medium",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:font-medium",
        ghost: "hover:bg-accent hover:text-accent-foreground hover:font-medium",
        link: "text-primary underline-offset-4 hover:underline hover:font-medium",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

// Define Props Interface using VariantProps and standard HTML attributes
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants }; 